import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import VitrineLayout from '../../components/layouts/VitrineLayout';
import {
  COPPER, COPPER_LIGHT, CREAM, DARK_BG, DARK_BG_2,
  DISPLAY_FF, ITALIC_FF, GRAIN_BG, GLASS_STYLE, COPPER_BTN_STYLE,
  CopperHalo, Eyebrow, Shimmer,
} from '../../components/vitrine/theme';

// === 6 categories × 3 realizations each = 18 premium projects
// Images chosen from Unsplash high-res editorial photography (royalty-free, premium feel)
const CATEGORIES = [
  {
    key: 'plans',
    label: 'Plans de travail',
    description: 'Cuisines & îlots sur-mesure',
    projects: [
      { title: 'Calacatta Veneziano', material: 'Marbre Calacatta Oro', image: '/realisations/plans-0.jpg' },
      { title: 'Cuisine Black Cosmos', material: 'Granit Black Cosmos', image: '/realisations/plans-1.jpg' },
      { title: 'Îlot Quartz Eternal', material: 'Silestone Eternal Calacatta', image: '/realisations/plans-2.jpg' },
    ],
  },
  {
    key: 'bains',
    label: 'Salles de bain',
    description: 'Volumes apaisés en pierre noble',
    projects: [
      { title: 'Suite Travertin Crema', material: 'Travertin Romain', image: '/realisations/bains-0.jpg' },
      { title: 'Salle d\'eau Carrare', material: 'Marbre Carrare Statuario', image: '/realisations/bains-1.jpg' },
      { title: 'Spa Marquina', material: 'Marbre Nero Marquina', image: '/realisations/bains-2.jpg' },
    ],
  },
  {
    key: 'tables',
    label: 'Tables',
    description: 'Pièces uniques pour la salle à manger',
    projects: [
      { title: 'Table monolithique Onyx', material: 'Onyx Honey', image: '/realisations/tables-0.jpg' },
      { title: 'Console Sahara Noir', material: 'Marbre Sahara Noir', image: '/realisations/tables-1.jpg' },
      { title: 'Table basse Travertin', material: 'Travertin Silver', image: '/realisations/tables-2.jpg' },
    ],
  },
  {
    key: 'escaliers',
    label: 'Escaliers',
    description: 'Architecture verticale en pierre massive',
    projects: [
      { title: 'Escalier hélicoïdal', material: 'Marbre Bianco Carrara', image: '/realisations/escaliers-0.jpg' },
      { title: 'Marches Crema Marfil', material: 'Marbre Crema Marfil', image: '/realisations/escaliers-1.jpg' },
      { title: 'Escalier suspendu Galaxy', material: 'Granit Black Galaxy', image: '/realisations/escaliers-2.jpg' },
    ],
  },
  {
    key: 'revetements',
    label: 'Revêtements',
    description: 'Sols, murs & habillages prestige',
    projects: [
      { title: 'Hall Calacatta Borghini', material: 'Marbre Calacatta Borghini', image: '/realisations/revetements-0.jpg' },
      { title: 'Mur Bronzo Imperiale', material: 'Xtone Bronzo Imperiale', image: '/realisations/revetements-1.jpg' },
      { title: 'Sol Travertin Beige', material: 'Travertin Classico', image: '/realisations/revetements-2.jpg' },
    ],
  },
  {
    key: 'vasques',
    label: 'Vasques',
    description: 'Sculptures fonctionnelles taillées dans la masse',
    projects: [
      { title: 'Vasque monobloc Carrare', material: 'Marbre Carrare', image: '/realisations/vasques-0.jpg' },
      { title: 'Double vasque Travertin', material: 'Travertin Noce', image: '/realisations/vasques-1.jpg' },
      { title: 'Vasque Dekton Kelya', material: 'Dekton Kelya', image: '/realisations/vasques-2.jpg' },
    ],
  },
];

// Build flat list with category info
const ALL_PROJECTS = CATEGORIES.flatMap((cat, ci) =>
  cat.projects.map((p, pi) => ({
    ...p,
    id: `${cat.key}-${pi}`,
    categoryKey: cat.key,
    categoryLabel: cat.label,
    index: ci * 3 + pi,
  }))
);

const CATEGORY_LABELS = CATEGORIES.map(c => c.label);

// === HERO === proeminent title + category subtitles
function RealizationsHero() {
  return (
    <section className="relative h-[38vh] min-h-[300px] lg:h-[44vh] overflow-hidden" data-testid="realizations-hero">
      <img
        src="/hero-granipal.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: 'brightness(0.55) saturate(0.95)' }}
      />
      <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18,11,7,0.45) 0%, rgba(18,11,7,0.30) 35%, rgba(18,11,7,0.92) 100%)' }} />
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />
      <CopperHalo x="50%" y="70%" size={1100} opacity={0.18} />

      <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-10 lg:pb-14">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <Eyebrow>Portfolio · Réalisations sur-mesure</Eyebrow>
          <h1
            style={{
              fontFamily: DISPLAY_FF,
              fontSize: 'clamp(2.6rem, 6.5vw, 5.5rem)',
              fontWeight: 400,
              color: CREAM,
              letterSpacing: '0.01em',
              lineHeight: 0.95,
              textShadow: '0 2px 18px rgba(0,0,0,0.55)',
            }}
          >
            Nos{' '}
            <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: ITALIC_FF }}>
              réalisations
            </em>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="mt-5 text-sm md:text-base font-light"
            style={{ color: 'rgba(245,230,208,0.78)', letterSpacing: '0.04em', maxWidth: '46rem' }}
          >
            {CATEGORY_LABELS.join(' · ')}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

// === FULL-WIDTH HORIZONTAL CINEMATIC CAROUSEL ===
// One project takes the whole viewport width on desktop. User scrolls horizontally
// with smooth snap. Numbers + nav controls overlay the imagery.
function CinematicCarousel({ projects, autoFilter }) {
  const filtered = autoFilter === 'all' ? projects : projects.filter(p => p.categoryKey === autoFilter);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);

  // Reset to 0 when filter changes
  useEffect(() => {
    setActiveIdx(0);
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
  }, [autoFilter]);

  // Update active index on scroll
  const handleScroll = () => {
    if (!scrollerRef.current) return;
    const sl = scrollerRef.current;
    const slideW = sl.clientWidth * 0.85; // matches w-[85vw]
    const idx = Math.round(sl.scrollLeft / slideW);
    if (idx !== activeIdx) setActiveIdx(idx);
  };

  const goTo = (dir) => {
    if (!scrollerRef.current) return;
    const sl = scrollerRef.current;
    const slideW = sl.clientWidth * 0.85;
    const next = Math.max(0, Math.min(filtered.length - 1, activeIdx + dir));
    sl.scrollTo({ left: next * slideW, behavior: 'smooth' });
    setActiveIdx(next);
  };

  const current = filtered[activeIdx];

  return (
    <div className="relative" data-testid="realizations-carousel">
      {/* Scroller — horizontal snap */}
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 lg:px-12 pb-2"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{`.cinematic-scroller::-webkit-scrollbar { display: none; }`}</style>
        {filtered.map((p, idx) => (
          <CinematicSlide key={p.id} project={p} index={idx} total={filtered.length} active={idx === activeIdx} />
        ))}
      </div>

      {/* Controls + counter */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-8 flex items-center justify-between gap-6">
        <div className="flex items-baseline gap-3">
          <span style={{ fontFamily: DISPLAY_FF, fontSize: '2.2rem', color: COPPER_LIGHT, lineHeight: 1 }}>
            {String(activeIdx + 1).padStart(2, '0')}
          </span>
          <span className="text-white/35 text-sm">/ {String(filtered.length).padStart(2, '0')}</span>
          <span className="hidden md:inline text-white/40 text-xs uppercase tracking-[0.3em] ml-6">
            {current?.categoryLabel}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => goTo(-1)}
            disabled={activeIdx === 0}
            aria-label="Précédent"
            className="w-12 h-12 flex items-center justify-center transition-all duration-500 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(184,137,90,0.10)',
              border: '1px solid rgba(212,168,120,0.40)',
              borderRadius: '2px',
              color: COPPER_LIGHT,
            }}
            data-testid="realizations-prev"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => goTo(1)}
            disabled={activeIdx === filtered.length - 1}
            aria-label="Suivant"
            className="w-12 h-12 flex items-center justify-center transition-all duration-500 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(184,137,90,0.10)',
              border: '1px solid rgba(212,168,120,0.40)',
              borderRadius: '2px',
              color: COPPER_LIGHT,
            }}
            data-testid="realizations-next"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress dots */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-4 flex gap-1.5">
        {filtered.map((_, i) => (
          <div
            key={i}
            className="h-px flex-1 transition-all duration-500"
            style={{
              background: i === activeIdx ? COPPER_LIGHT : 'rgba(245,230,208,0.10)',
              transform: i === activeIdx ? 'scaleY(2)' : 'scaleY(1)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CinematicSlide({ project, active }) {
  return (
    <motion.article
      className="relative shrink-0 w-[85vw] max-w-[1200px] snap-center overflow-hidden"
      style={{
        aspectRatio: '16/9',
        background: 'rgba(18,11,7,0.4)',
        border: '1px solid rgba(245,230,208,0.06)',
      }}
      data-testid={`realization-${project.id}`}
    >
      <motion.img
        src={project.image}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        animate={{ scale: active ? 1.04 : 1.0 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        style={{ filter: 'saturate(0.95) contrast(1.05)' }}
      />
      {/* Cinematic gradient: dark at bottom for text readability */}
      <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(18,11,7,0) 35%, rgba(18,11,7,0.55) 70%, rgba(18,11,7,0.92) 100%)' }} />
      {/* Side vignette */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(18,11,7,0.25) 100%)' }} />

      {/* Floating badge: category */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: active ? 1 : 0.6, x: 0 }}
        transition={{ duration: 0.8 }}
        className="absolute top-6 left-6 lg:top-8 lg:left-8 px-3 py-1.5"
        style={{
          background: 'rgba(20,11,7,0.65)',
          border: '1px solid rgba(212,168,120,0.50)',
          backdropFilter: 'blur(12px)',
          color: COPPER_LIGHT,
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.32em',
        }}
      >
        {project.categoryLabel}
      </motion.div>

      {/* Title + material at bottom */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: active ? 1 : 0.7, y: active ? 0 : 10 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 max-w-3xl"
      >
        <h2
          style={{
            fontFamily: DISPLAY_FF,
            fontSize: 'clamp(1.6rem, 3.5vw, 3rem)',
            fontWeight: 400,
            color: CREAM,
            letterSpacing: '0.01em',
            lineHeight: 1.05,
            textShadow: '0 2px 14px rgba(0,0,0,0.55)',
          }}
        >
          {project.title}
        </h2>
        <div className="mt-2 inline-flex items-center gap-2.5">
          <span aria-hidden className="block h-px w-6" style={{ background: COPPER_LIGHT }} />
          <span className="text-[12px] tracking-[0.22em]" style={{ color: 'rgba(245,230,208,0.72)' }}>
            {project.material}
          </span>
        </div>
      </motion.div>
    </motion.article>
  );
}

// === MASONRY GALLERY ===
// Premium artistic layout with varied tile sizes per project. Filters per category.
function MasonryGallery({ projects, autoFilter }) {
  const filtered = autoFilter === 'all' ? projects : projects.filter(p => p.categoryKey === autoFilter);

  // Pattern of tile sizes — repeats across the gallery for an artistic mix.
  // Each tile has a row-span and col-span on lg.
  // Pattern: tall · wide · square · tall-wide · square · tall · wide · square · tall
  const sizePattern = [
    'lg:col-span-3 lg:row-span-2',  // 0 large wide
    'lg:col-span-3 lg:row-span-1',  // 1 wide short
    'lg:col-span-3 lg:row-span-2',  // 2 large wide
    'lg:col-span-2 lg:row-span-2',  // 3 standard
    'lg:col-span-2 lg:row-span-1',  // 4 short
    'lg:col-span-2 lg:row-span-2',  // 5 standard
    'lg:col-span-4 lg:row-span-2',  // 6 wide hero
    'lg:col-span-2 lg:row-span-2',  // 7 small
    'lg:col-span-3 lg:row-span-1',  // 8 short
    'lg:col-span-3 lg:row-span-2',  // 9 medium
    'lg:col-span-3 lg:row-span-2',  // 10 medium
    'lg:col-span-2 lg:row-span-1',  // 11 short
  ];

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 auto-rows-[180px] lg:auto-rows-[220px] gap-3 lg:gap-4 max-w-7xl mx-auto px-6 lg:px-12"
      data-testid="realizations-masonry"
    >
      {filtered.map((p, i) => (
        <MasonryTile
          key={p.id}
          project={p}
          sizeClass={sizePattern[i % sizePattern.length]}
          delay={Math.min(i * 0.05, 0.4)}
        />
      ))}
    </div>
  );
}

function MasonryTile({ project, sizeClass, delay }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden cursor-pointer ${sizeClass}`}
      style={{
        background: 'rgba(18,11,7,0.4)',
        border: '1px solid rgba(245,230,208,0.06)',
      }}
      data-testid={`realization-${project.id}`}
    >
      <img
        src={project.image}
        alt={project.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
        style={{ filter: 'saturate(0.95) contrast(1.05)' }}
      />
      {/* Dark wash for readability */}
      <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(18,11,7,0) 40%, rgba(18,11,7,0.45) 70%, rgba(18,11,7,0.88) 100%)' }} />
      {/* Copper sheen on hover */}
      <div aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(184,137,90,0.20), transparent 70%)' }} />

      {/* Category badge top-left, only on hover */}
      <div
        aria-hidden
        className="absolute top-3 left-3 px-2.5 py-1 text-[9px] uppercase tracking-[0.24em] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'rgba(20,11,7,0.7)',
          border: '1px solid rgba(212,168,120,0.50)',
          backdropFilter: 'blur(8px)',
          color: COPPER_LIGHT,
        }}
      >
        {project.categoryLabel}
      </div>

      {/* Title + material at bottom */}
      <div className="absolute inset-x-0 bottom-0 px-4 py-4 lg:px-5 lg:py-5">
        <h2
          style={{
            fontFamily: DISPLAY_FF,
            fontSize: 'clamp(1rem, 1.4vw, 1.45rem)',
            fontWeight: 400,
            color: CREAM,
            letterSpacing: '0.01em',
            lineHeight: 1.1,
            textShadow: '0 2px 12px rgba(0,0,0,0.65)',
          }}
        >
          {project.title}
        </h2>
        <div className="mt-1.5 inline-flex items-center gap-2">
          <span aria-hidden className="block h-px w-4" style={{ background: COPPER_LIGHT }} />
          <span className="text-[10px] tracking-[0.22em]" style={{ color: 'rgba(245,230,208,0.78)' }}>
            {project.material}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

// === FILTER PILLS ===
function CategoryPills({ active, onChange }) {
  const items = [
    { key: 'all', label: 'Toutes' },
    ...CATEGORIES.map(c => ({ key: c.key, label: c.label })),
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-2.5 lg:gap-3" data-testid="realizations-categories">
      {items.map((it) => {
        const isActive = it.key === active;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onChange(it.key)}
            className="group relative px-5 py-2.5 text-[11px] uppercase tracking-[0.28em] font-semibold transition-all duration-500"
            style={{
              color: isActive ? '#FFF8EC' : 'rgba(245,230,208,0.72)',
              background: isActive
                ? 'linear-gradient(135deg, rgba(212,168,120,0.92) 0%, rgba(184,137,90,0.96) 100%)'
                : 'rgba(184,137,90,0.08)',
              border: `1px solid ${isActive ? 'rgba(245,230,208,0.55)' : 'rgba(212,168,120,0.30)'}`,
              borderRadius: '2px',
              backdropFilter: 'blur(8px)',
              boxShadow: isActive
                ? 'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 22px -8px rgba(184,137,90,0.55)'
                : 'inset 0 1px 0 rgba(255,250,240,0.06)',
            }}
            data-testid={`cat-${it.key}`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export default function RealizationsPage() {
  const [activeCat, setActiveCat] = useState('all');

  return (
    <VitrineLayout>
      <RealizationsHero />

      {/* Main gallery section */}
      <section className="relative py-16 lg:py-24 overflow-hidden" style={{ background: DARK_BG }} data-testid="realizations-main">
        <CopperHalo x="20%" y="10%" size={700} opacity={0.05} />
        <CopperHalo x="80%" y="80%" size={700} opacity={0.05} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative">
          {/* Categories filter */}
          <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-10 lg:mb-12">
            <CategoryPills active={activeCat} onChange={setActiveCat} />
          </div>

          {/* The premium masonry gallery */}
          <MasonryGallery projects={ALL_PROJECTS} autoFilter={activeCat} />
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative py-20 lg:py-24 overflow-hidden" style={{ background: DARK_BG_2 }} data-testid="realizations-stats">
        <CopperHalo x="50%" y="50%" size={1100} opacity={0.14} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {[
              { value: '500+', label: 'Projets réalisés' },
              { value: '25+',  label: "Années d'expérience" },
              { value: '100%', label: 'Satisfaction client' },
              { value: '50+',  label: 'Références de pierre' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.4 }}
                className="relative px-6 py-8 text-center"
                style={GLASS_STYLE}
              >
                <div style={{ fontFamily: DISPLAY_FF, fontSize: 'clamp(2rem, 3.6vw, 2.6rem)', color: COPPER_LIGHT, letterSpacing: '0.01em', lineHeight: 1 }}>
                  {stat.value}
                </div>
                <div className="mt-3 text-[10.5px] uppercase tracking-[0.32em]" style={{ color: CREAM }}>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 lg:py-20 text-center overflow-hidden" style={{ background: DARK_BG }}>
        <div className="relative max-w-3xl mx-auto px-6">
          <Eyebrow>Votre projet</Eyebrow>
          <h2
            className="mt-3"
            style={{
              fontFamily: DISPLAY_FF,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              fontWeight: 400,
              color: CREAM,
              lineHeight: 1.1,
              letterSpacing: '0.01em',
            }}
          >
            Imaginons votre{' '}
            <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontFamily: ITALIC_FF, fontWeight: 400 }}>prochaine réalisation</em>
          </h2>
          <Link
            to="/contact"
            className="group relative inline-flex items-center gap-3 mt-8 px-8 py-3.5 text-[11px] uppercase tracking-[0.28em] font-light overflow-hidden transition-all duration-500"
            style={COPPER_BTN_STYLE}
            data-testid="realizations-cta-contact"
          >
            <Shimmer />
            <span className="relative">Nous contacter</span>
            <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
          </Link>
        </div>
      </section>
    </VitrineLayout>
  );
}
